import { useCallback, useRef, useState, type MutableRefObject } from 'react';

import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support/jira';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { KBArticleData } from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-kb-sync-commands';

import { createKbApi } from './createKbApi';
import type { KbTypeOption } from './CreateKBCardForm';
import type { SpaceOption } from './CreateKBCardPublishForm';
import { publishConfluenceArticle } from './publishConfluenceArticle.ts';
import { isCreateKbLinkSourceAlreadyHasContentError } from './useCreateKbSourceLinking';

type KbSourceVisibility = KbTypeOption['value'];

type LinkSpaceToKnowledgeBase = (args: {
	cloudId: string;
	projectIdentifier: string;
	spaceId: string;
	sourceVisibility: KbSourceVisibility;
	appLinkId: string | null;
}) => Promise<void>;

type ConfirmCreateSpaceArgs = {
	spaceName: string;
	selectedTypeValue: KbSourceVisibility;
};

type PublishArgs = {
	publishNewSpaceName: string | null;
	isPublishToNewSpaceChecked: boolean;
	selectedSpace: SpaceOption | null;
	selectedTypeValue: KbSourceVisibility;
};

type UseCreateKbPublishHandlersResult = {
	isConfirmLoading: boolean;
	handleConfirmCreateSpace: (args: ConfirmCreateSpaceArgs) => Promise<void>;
	handlePublish: (args: PublishArgs) => Promise<void>;
};

type PublishStep = 'activateAppLink' | 'createSpace' | 'linkSpace' | 'publishArticles';

type CreatedSpace = {
	id: string;
	key: string;
};

type RetryState = {
	signature: string;
	failedSteps: PublishStep[];
	failedArticleKeys: string[];
	createdSpace: CreatedSpace | null;
};

type RetryArticle = {
	article: KBArticleData;
	retryKey: string;
};

type LinkSpaceParams = {
	cloudId: string;
	projectIdentifier: string;
	spaceId: string;
	sourceVisibility: KbSourceVisibility;
	appLinkId: string | null;
};

type RetryExecutionContext = {
	shouldRetryFailedStepsOnly: boolean;
	shouldRunStep: (step: PublishStep) => boolean;
	setCreatedSpace: (space: CreatedSpace) => void;
	getCreatedSpace: () => CreatedSpace | null;
	failStep: (step: PublishStep) => void;
	failPublishArticles: (articleKeys: string[]) => void;
	clearStepFailure: (step: PublishStep) => void;
	clearRetryState: () => void;
	selectArticlesToPublish: (articles: KBArticleData[]) => RetryArticle[];
};

const CONFLUENCE_CROSS_FLOW_SOURCE_COMPONENT = 'JSDKnowledgeBaseSetupXFlowUi';
const SPACE_LINK_PROPAGATION_DELAY_MS = 500;

const waitForSpaceLinkPropagation = () =>
	new Promise<void>((resolve) => {
		setTimeout(resolve, SPACE_LINK_PROPAGATION_DELAY_MS);
	});

const waitForSpaceLinkPropagationIfEnabled = async () => {
	if (!fg('jsm_kb_from_scratch_space_link_delay')) {
		return;
	}

	// Backend linkage is not immediately consistent; without a brief wait the article publish API
	// can return 4xx responses right after the space link succeeds.
	await waitForSpaceLinkPropagation();
};

const retryStepsFrom: Record<PublishStep, PublishStep[]> = {
	activateAppLink: ['activateAppLink', 'createSpace', 'linkSpace', 'publishArticles'],
	createSpace: ['createSpace', 'linkSpace', 'publishArticles'],
	linkSpace: ['linkSpace', 'publishArticles'],
	publishArticles: ['publishArticles'],
};

const getArticlesFingerprint = (articles: KBArticleData[]) =>
	articles.map((article, index) => ({
		key: article.articleId,
		title: article.title,
		summary: article.summary ?? null,
		body: article.body ?? null,
		rovoTopicId: article.rovoTopicId ?? null,
	}));

const createRetryExecutionContext = ({
	retryStateRef,
	signature,
}: {
	retryStateRef: MutableRefObject<RetryState | null>;
	signature: string;
}): RetryExecutionContext => {
	const existingRetryState = retryStateRef.current;
	const shouldRetryFailedStepsOnly =
		existingRetryState?.signature === signature && existingRetryState.failedSteps.length > 0;
	const failedSteps = new Set<PublishStep>(
		shouldRetryFailedStepsOnly ? existingRetryState.failedSteps : [],
	);
	const failedArticleKeys = new Set<string>(
		shouldRetryFailedStepsOnly ? existingRetryState.failedArticleKeys : [],
	);
	let createdSpace: CreatedSpace | null =
		shouldRetryFailedStepsOnly && existingRetryState ? existingRetryState.createdSpace : null;

	const persistFailureState = () => {
		retryStateRef.current = {
			signature,
			failedSteps: Array.from(failedSteps),
			failedArticleKeys: Array.from(failedArticleKeys),
			createdSpace,
		};
	};

	return {
		shouldRetryFailedStepsOnly,
		shouldRunStep: (step: PublishStep) => !shouldRetryFailedStepsOnly || failedSteps.has(step),
		setCreatedSpace: (space: CreatedSpace) => {
			createdSpace = space;
		},
		getCreatedSpace: () => createdSpace,
		failStep: (step: PublishStep) => {
			failedSteps.clear();
			retryStepsFrom[step].forEach((retryStep) => {
				failedSteps.add(retryStep);
			});
			failedArticleKeys.clear();
			persistFailureState();
		},
		failPublishArticles: (articleKeys: string[]) => {
			failedSteps.clear();
			failedSteps.add('publishArticles');
			failedArticleKeys.clear();
			articleKeys.forEach((articleKey) => {
				failedArticleKeys.add(articleKey);
			});
			persistFailureState();
		},
		clearStepFailure: (step: PublishStep) => {
			failedSteps.delete(step);
		},
		clearRetryState: () => {
			retryStateRef.current = null;
		},
		selectArticlesToPublish: (articles: KBArticleData[]) =>
			articles
				.map((article, index) => ({
					article,
					retryKey: article.articleId,
				}))
				.filter(({ retryKey }) => {
					if (!shouldRetryFailedStepsOnly) {
						return true;
					}

					if (!failedSteps.has('publishArticles')) {
						return false;
					}

					if (failedArticleKeys.size === 0) {
						return true;
					}

					return failedArticleKeys.has(retryKey);
				}),
	};
};

const runRetryableStep = async ({
	context,
	step,
	execute,
}: {
	context: RetryExecutionContext;
	step: PublishStep;
	execute: () => Promise<void>;
}): Promise<void> => {
	if (!context.shouldRunStep(step)) {
		return;
	}

	try {
		await execute();
		context.clearStepFailure(step);
	} catch (error) {
		context.failStep(step);
		throw error;
	}
};

const getFailedArticleKeys = (
	publishResults: PromiseSettledResult<void>[],
	articles: RetryArticle[],
) =>
	publishResults
		.map((result, index) => ({
			result,
			articleKey: articles[index].retryKey,
		}))
		.filter(({ result }) => result.status === 'rejected')
		.map(({ articleKey }) => articleKey);

const getFirstRejectedReason = (publishResults: PromiseSettledResult<void>[]) => {
	const rejectedResult = publishResults.find(
		(result): result is PromiseRejectedResult => result.status === 'rejected',
	);
	return rejectedResult?.reason;
};

const getLinkSpaceSignature = (params: LinkSpaceParams) =>
	JSON.stringify({
		cloudId: params.cloudId,
		projectIdentifier: params.projectIdentifier,
		spaceId: params.spaceId,
		sourceVisibility: params.sourceVisibility,
		appLinkId: params.appLinkId,
	});

export const useCreateKbPublishHandlers = ({
	cloudId,
	projectId,
	projectIdentifier,
	jiraActivationId,
	appLinkId,
	stagingAreaArticles,
	linkSpaceToKnowledgeBase,
	closeModal,
	onLinkSourceAlreadyHasContentErrorChange,
	onGenericPublishErrorChange,
}: {
	cloudId: string;
	projectId: number;
	projectIdentifier: string | null;
	jiraActivationId?: string;
	appLinkId: string | null;
	stagingAreaArticles: KBArticleData[];
	linkSpaceToKnowledgeBase: LinkSpaceToKnowledgeBase;
	closeModal: () => void;
	onLinkSourceAlreadyHasContentErrorChange: (hasError: boolean) => void;
	onGenericPublishErrorChange: (hasError: boolean) => void;
}): UseCreateKbPublishHandlersResult => {
	const [isConfirmLoading, setIsConfirmLoading] = useState(false);
	const crossFlow = useCrossFlow();
	const { sendAnalyticsEvent } = useAnalytics();
	const confirmRetryStateRef = useRef<RetryState | null>(null);
	const publishRetryStateRef = useRef<RetryState | null>(null);
	const confirmLastSuccessfulLinkSignatureRef = useRef<string | null>(null);
	const publishLastSuccessfulLinkSignatureRef = useRef<string | null>(null);

	const handleConfirmCreateSpace = useCallback(
		async ({ spaceName, selectedTypeValue }: ConfirmCreateSpaceArgs) => {
			if (!spaceName.trim() || isConfirmLoading || !projectIdentifier || !jiraActivationId) {
				return;
			}

			try {
				onLinkSourceAlreadyHasContentErrorChange(false);
				onGenericPublishErrorChange(false);
				setIsConfirmLoading(true);
				const normalizedSpaceName = spaceName.trim();
				const normalizedArticles = stagingAreaArticles ?? [];
				const confirmSignature = JSON.stringify({
					spaceName: normalizedSpaceName,
					selectedTypeValue,
					projectIdentifier,
					jiraActivationId,
					appLinkId,
					articles: getArticlesFingerprint(normalizedArticles),
				});
				const retryContext = createRetryExecutionContext({
					retryStateRef: confirmRetryStateRef,
					signature: confirmSignature,
				});

				await runRetryableStep({
					context: retryContext,
					step: 'createSpace',
					execute: async () => {
						const newlyCreatedSpace = await createKbApi.createConfluenceSpace({
							spaceName: normalizedSpaceName,
							projectId,
							activationId: jiraActivationId,
						});
						retryContext.setCreatedSpace({
							id: newlyCreatedSpace.id,
							key: newlyCreatedSpace.key,
						});
					},
				});

				const createdSpace = retryContext.getCreatedSpace();
				if (!createdSpace) {
					const error = new Error('Unable to resolve created space for confirm publish flow');
					retryContext.failStep('createSpace');
					throw error;
				}

				const linkSpaceParams: LinkSpaceParams = {
					cloudId,
					projectIdentifier,
					spaceId: createdSpace.id,
					sourceVisibility: selectedTypeValue,
					appLinkId,
				};
				const linkSpaceSignature = getLinkSpaceSignature(linkSpaceParams);
				if (confirmLastSuccessfulLinkSignatureRef.current === linkSpaceSignature) {
					retryContext.clearStepFailure('linkSpace');
				} else {
					await runRetryableStep({
						context: retryContext,
						step: 'linkSpace',
						execute: async () => {
							await linkSpaceToKnowledgeBase(linkSpaceParams);
							confirmLastSuccessfulLinkSignatureRef.current = linkSpaceSignature;
						},
					});
					await waitForSpaceLinkPropagationIfEnabled();
				}

				const articlesToPublish = retryContext.selectArticlesToPublish(normalizedArticles);

				if (articlesToPublish.length > 0) {
					const publishResults = await Promise.allSettled(
						articlesToPublish.map(({ article }) =>
							publishConfluenceArticle({
								projectId,
								activationId: jiraActivationId,
								spaceKey: createdSpace.key,
								article,
								status: 'DRAFT',
							}),
						),
					);
					const nextFailedArticleKeys = getFailedArticleKeys(publishResults, articlesToPublish);

					if (nextFailedArticleKeys.length > 0) {
						retryContext.failPublishArticles(nextFailedArticleKeys);
						throw (
							getFirstRejectedReason(publishResults) ??
							new Error('Failed to publish one or more articles')
						);
					}
				}

				retryContext.clearRetryState();
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'published',
					actionSubject: 'jsmKbFromScratchArticles',
					actionSubjectId: 'jsmKbFromScratchArticlesPublished',
					attributes: {
						projectId,
						activationId: jiraActivationId,
						spaceId: createdSpace.id,
						spaceKey: createdSpace.key,
						publishStatus: 'DRAFT',
						articleCount: stagingAreaArticles.length,
						sourceVisibility: selectedTypeValue,
					},
				});

				closeModal();
			} catch (error) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'jsmKbFromScratchArticles',
					actionSubjectId: 'jsmKbFromScratchArticlesPublishFailed',
					attributes: {
						projectId,
						activationId: jiraActivationId,
						publishStatus: 'DRAFT',
						articleCount: stagingAreaArticles.length,
						sourceVisibility: selectedTypeValue,
					},
				});
				const isLinkSourceAlreadyHasContentError =
					isCreateKbLinkSourceAlreadyHasContentError(error);
				onLinkSourceAlreadyHasContentErrorChange(isLinkSourceAlreadyHasContentError);
				onGenericPublishErrorChange(!isLinkSourceAlreadyHasContentError);
				logException({
					exception: error,
					name: 'CreateKbCardConfirmCreateSpaceError',
				});
			} finally {
				setIsConfirmLoading(false);
			}
		},
		[
			appLinkId,
			closeModal,
			cloudId,
			isConfirmLoading,
			jiraActivationId,
			linkSpaceToKnowledgeBase,
			onGenericPublishErrorChange,
			onLinkSourceAlreadyHasContentErrorChange,
			projectId,
			projectIdentifier,
			sendAnalyticsEvent,
			stagingAreaArticles,
		],
	);

	const handlePublish = useCallback(
		async ({
			publishNewSpaceName,
			isPublishToNewSpaceChecked,
			selectedSpace,
			selectedTypeValue,
		}: PublishArgs) => {
			if (isConfirmLoading || !jiraActivationId || !projectIdentifier) {
				return;
			}

			try {
				onLinkSourceAlreadyHasContentErrorChange(false);
				onGenericPublishErrorChange(false);
				setIsConfirmLoading(true);

				const newSpaceName = publishNewSpaceName?.trim() ?? '';
				const shouldCreateNewSpace = newSpaceName.length > 0;
				const shouldActivateAppLink =
					shouldCreateNewSpace &&
					!appLinkId &&
					isPublishToNewSpaceChecked &&
					fg('jsm_kb_from_scratch_confluence_linking');
				const normalizedArticles = stagingAreaArticles ?? [];
				const publishSignature = JSON.stringify({
					publishNewSpaceName: newSpaceName,
					isPublishToNewSpaceChecked,
					selectedSpaceId: selectedSpace?.value ?? null,
					selectedSpaceKey: selectedSpace?.key ?? null,
					selectedTypeValue,
					projectIdentifier,
					jiraActivationId,
					appLinkId,
					articles: getArticlesFingerprint(normalizedArticles),
				});
				const retryContext = createRetryExecutionContext({
					retryStateRef: publishRetryStateRef,
					signature: publishSignature,
				});

				let targetSpaceId = selectedSpace?.value ?? '';
				let targetSpaceKey = selectedSpace?.key ?? '';
				const createdSpace = retryContext.getCreatedSpace();
				if (createdSpace) {
					targetSpaceId = createdSpace.id;
					targetSpaceKey = createdSpace.key;
				}

				if (shouldCreateNewSpace) {
					if (shouldActivateAppLink) {
						await runRetryableStep({
							context: retryContext,
							step: 'activateAppLink',
							execute: async () => {
								if (!crossFlow.isEnabled) {
									sendAnalyticsEvent({
										eventType: 'track',
										action: 'failed',
										actionSubject: 'jsmKbConfluenceCrossFlowActivation',
										actionSubjectId: 'jsmKbConfluenceCrossFlowActivationFailed',
										attributes: {
											projectId,
											activationId: jiraActivationId,
											sourceComponent: CONFLUENCE_CROSS_FLOW_SOURCE_COMPONENT,
											reason: 'crossFlowDisabled',
										},
									});
									throw new Error('Confluence cross-flow is unavailable');
								}

								const status = await crossFlow.api.open({
									journey: Journeys.GET_STARTED,
									targetProduct: Targets.CONFLUENCE,
									sourceContext: 'jira',
									sourceComponent: CONFLUENCE_CROSS_FLOW_SOURCE_COMPONENT,
									experimentalOptions: {
										isJSMKnowledgeBaseRollout: true,
									},
								});

								if (status.success === true) {
									sendAnalyticsEvent({
										eventType: 'track',
										action: 'activated',
										actionSubject: 'jsmKbConfluenceCrossFlowActivation',
										actionSubjectId: 'jsmKbConfluenceCrossFlowActivationSucceeded',
										attributes: {
											projectId,
											activationId: jiraActivationId,
											sourceComponent: CONFLUENCE_CROSS_FLOW_SOURCE_COMPONENT,
										},
									});
									return;
								}

								sendAnalyticsEvent({
									eventType: 'track',
									action: 'failed',
									actionSubject: 'jsmKbConfluenceCrossFlowActivation',
									actionSubjectId: 'jsmKbConfluenceCrossFlowActivationFailed',
									attributes: {
										projectId,
										activationId: jiraActivationId,
										sourceComponent: CONFLUENCE_CROSS_FLOW_SOURCE_COMPONENT,
										reason: 'notActivated',
									},
								});
								throw new Error('Confluence cross-flow activation was not completed');
							},
						});
					}

					await runRetryableStep({
						context: retryContext,
						step: 'createSpace',
						execute: async () => {
							const newlyCreatedSpace = await createKbApi.createConfluenceSpace({
								spaceName: newSpaceName,
								projectId,
								activationId: jiraActivationId,
							});
							const nextCreatedSpace = {
								id: newlyCreatedSpace.id,
								key: newlyCreatedSpace.key,
							};
							retryContext.setCreatedSpace(nextCreatedSpace);
							targetSpaceId = nextCreatedSpace.id;
							targetSpaceKey = nextCreatedSpace.key;
						},
					});
				} else if (!targetSpaceId || !targetSpaceKey) {
					return;
				}

				if (shouldCreateNewSpace && (!targetSpaceId || !targetSpaceKey)) {
					const error = new Error('Unable to resolve target space for publish flow');
					retryContext.failStep('createSpace');
					throw error;
				}

				const linkSpaceParams: LinkSpaceParams = {
					cloudId,
					projectIdentifier,
					spaceId: targetSpaceId,
					sourceVisibility: selectedTypeValue,
					appLinkId,
				};
				const linkSpaceSignature = getLinkSpaceSignature(linkSpaceParams);
				if (publishLastSuccessfulLinkSignatureRef.current === linkSpaceSignature) {
					retryContext.clearStepFailure('linkSpace');
				} else {
					await runRetryableStep({
						context: retryContext,
						step: 'linkSpace',
						execute: async () => {
							await linkSpaceToKnowledgeBase(linkSpaceParams);
							publishLastSuccessfulLinkSignatureRef.current = linkSpaceSignature;
						},
					});
					await waitForSpaceLinkPropagationIfEnabled();
				}

				const articlesToPublish = retryContext.selectArticlesToPublish(normalizedArticles);

				if (articlesToPublish.length > 0) {
					const publishResults = await Promise.allSettled(
						articlesToPublish.map(({ article }) =>
							publishConfluenceArticle({
								projectId,
								activationId: jiraActivationId,
								spaceKey: targetSpaceKey,
								article,
								status: 'CURRENT',
							}),
						),
					);
					const nextFailedArticleKeys = getFailedArticleKeys(publishResults, articlesToPublish);

					if (nextFailedArticleKeys.length > 0) {
						retryContext.failPublishArticles(nextFailedArticleKeys);
						throw (
							getFirstRejectedReason(publishResults) ??
							new Error('Failed to publish one or more articles')
						);
					}
				}

				retryContext.clearRetryState();
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'published',
					actionSubject: 'jsmKbFromScratchArticles',
					actionSubjectId: 'jsmKbFromScratchArticlesPublished',
					attributes: {
						projectId,
						activationId: jiraActivationId,
						spaceId: targetSpaceId,
						spaceKey: targetSpaceKey,
						publishStatus: 'CURRENT',
						articleCount: stagingAreaArticles.length,
						sourceVisibility: selectedTypeValue,
					},
				});

				closeModal();
			} catch (error) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'jsmKbFromScratchArticles',
					actionSubjectId: 'jsmKbFromScratchArticlesPublishFailed',
					attributes: {
						projectId,
						activationId: jiraActivationId,
						publishStatus: 'CURRENT',
						articleCount: stagingAreaArticles.length,
						sourceVisibility: selectedTypeValue,
					},
				});
				const isLinkSourceAlreadyHasContentError =
					isCreateKbLinkSourceAlreadyHasContentError(error);
				onLinkSourceAlreadyHasContentErrorChange(isLinkSourceAlreadyHasContentError);
				onGenericPublishErrorChange(!isLinkSourceAlreadyHasContentError);
				logException({
					exception: error,
					name: 'CreateKbCardPublishError',
				});
			} finally {
				setIsConfirmLoading(false);
			}
		},
		[
			appLinkId,
			closeModal,
			cloudId,
			crossFlow,
			isConfirmLoading,
			jiraActivationId,
			linkSpaceToKnowledgeBase,
			onGenericPublishErrorChange,
			onLinkSourceAlreadyHasContentErrorChange,
			projectId,
			projectIdentifier,
			sendAnalyticsEvent,
			stagingAreaArticles,
		],
	);

	return {
		isConfirmLoading,
		handleConfirmCreateSpace,
		handlePublish,
	};
};
